import React from 'react';
import { CheckCircle } from 'lucide-react';
import { Card } from '../ui/Card';

/**
 * OptimizationSuggestionCard - AI advisor card with conversational tone
 *
 * Shows optimization suggestions or confirmation that settings are optimal.
 * Displays above the product matrix in the fitter flow.
 *
 * @param {Object} props
 * @param {string} props.clubModel - Name of the current club
 * @param {number} props.currentLoft - Current loft setting
 * @param {Object} props.optimizationData - Results from optimizer
 * @param {number} props.optimizationData.recommended_loft - Recommended loft
 * @param {number} props.optimizationData.expected_gain_yards - Expected distance gain
 * @param {Object} props.optimizationData.metrics - Optimized metrics
 * @param {Object} props.optimizationData.current_metrics - Current metrics
 * @param {Function} props.onProceed - Callback when user proceeds to matrix
 */
const OptimizationSuggestionCard = ({
  clubModel,
  currentLoft,
  optimizationData,
  onProceed
}) => {
  if (!optimizationData) {
    return null;
  }

  const {
    recommended_loft,
    expected_gain_yards,
    metrics,
    current_metrics
  } = optimizationData;

  const gainYards = parseFloat(expected_gain_yards || 0);
  const isOptimal = gainYards <= 1; // 1 yard or less = already optimal (matches ROI threshold)

  // Calculate the adjustment needed
  const loftAdjustment = recommended_loft - currentLoft;

  if (isOptimal) {
    // Already optimal - show confirmation
    return (
      <Card className="max-w-4xl mx-auto bg-gradient-to-br from-success/10 to-transparent border-success/30">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-success/20 rounded-full shrink-0">
            <CheckCircle className="w-6 h-6 text-success" />
          </div>
          <div className="flex-1 space-y-4">
            <div>
              <h3 className="text-xl font-bold text-white mb-2">
                ✅ Your Settings Look Dialed In
              </h3>
              <p className="text-zinc-300 leading-relaxed">
                Your current {clubModel} at {currentLoft}° is already optimized for your swing.
                We don't see any meaningful gains from adjusting your loft sleeve.
              </p>
            </div>

            {current_metrics && (
              <div className="grid grid-cols-4 gap-3 p-4 bg-subtle/50 rounded-lg">
                <div>
                  <div className="text-xs text-zinc-500 mb-1">Launch</div>
                  <div className="text-white font-medium">{current_metrics.launch_angle}°</div>
                </div>
                <div>
                  <div className="text-xs text-zinc-500 mb-1">Spin</div>
                  <div className="text-white font-medium">{current_metrics.spin_rate} rpm</div>
                </div>
                <div>
                  <div className="text-xs text-zinc-500 mb-1">Carry</div>
                  <div className="text-white font-medium">{current_metrics.carry_distance} yds</div>
                </div>
                <div>
                  <div className="text-xs text-zinc-500 mb-1">Total</div>
                  <div className="text-white font-medium">{current_metrics.total_distance} yds</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
    );
  }

  // Has optimization opportunity
  return (
    <Card className="max-w-4xl mx-auto bg-gradient-to-br from-blue-500/10 to-transparent border-blue-500/30">
      <div className="flex-1 space-y-3">
          {/* Main suggestion */}
          <div>
            <h3 className="text-lg font-bold text-white mb-1.5">
              🔧 Before You Buy Anything...
            </h3>
            <p className="text-sm text-zinc-300 leading-snug">
              Try setting your <span className="text-white font-medium">{clubModel}</span> to{' '}
              <span className="text-blue-400 font-bold">{recommended_loft}°</span>.{' '}
              Projected gain: <span className="text-success font-bold">+{gainYards} yds</span> carry.
            </p>
          </div>

          {/* Details - Always Visible */}
          <div className="space-y-2 pt-2 border-t border-subtle">
            <div className="text-xs font-medium text-zinc-400 mb-2">
              What Changes?
            </div>

            {/* Metrics Comparison */}
            <div className="space-y-1.5">
              {current_metrics && metrics && (
                <>
                  <MetricRow
                    label="Launch Angle"
                    current={current_metrics.launch_angle}
                    optimized={metrics.launch_angle}
                    unit="°"
                    change={metrics.launch_angle - current_metrics.launch_angle}
                  />
                  <MetricRow
                    label="Spin Rate"
                    current={current_metrics.spin_rate}
                    optimized={metrics.spin_rate}
                    unit=" rpm"
                    change={metrics.spin_rate - current_metrics.spin_rate}
                  />
                  <MetricRow
                    label="Carry Distance"
                    current={current_metrics.carry_distance || 0}
                    optimized={metrics.carry_distance || 0}
                    unit=" yds"
                    change={(metrics.carry_distance || 0) - (current_metrics.carry_distance || 0)}
                    highlight
                  />
                  <MetricRow
                    label="Total Distance"
                    current={current_metrics.total_distance || 0}
                    optimized={metrics.total_distance || 0}
                    unit=" yds"
                    change={(metrics.total_distance || 0) - (current_metrics.total_distance || 0)}
                    highlight
                  />
                  <MetricRow
                    label="Descent Angle"
                    current={current_metrics.descent_angle}
                    optimized={metrics.descent_angle}
                    unit="°"
                    change={metrics.descent_angle - current_metrics.descent_angle}
                    higherIsBetter={true}
                  />
                </>
              )}
            </div>

            {/* Adjustment Instructions - Compact */}
            <div className="space-y-2">
              {loftAdjustment !== 0 && (
                <div className="p-2.5 bg-blue-500/10 rounded border border-blue-500/30">
                  <div className="text-xs text-zinc-300">
                    💡 {loftAdjustment > 0 ? (
                      <>Add <span className="text-blue-400 font-bold">+{loftAdjustment.toFixed(1)}°</span> using loft sleeve</>
                    ) : (
                      <>Reduce by <span className="text-blue-400 font-bold">{Math.abs(loftAdjustment).toFixed(1)}°</span> using loft sleeve</>
                    )}
                  </div>
                </div>
              )}

              {/* Bias/Shot Shape Advice */}
              <div className="p-2.5 bg-amber-500/10 rounded border border-amber-500/30">
                <div className="text-xs text-zinc-300">
                  🎯 <span className="font-medium">Fixing Slice/Hook:</span> Adjust hosel bias (Draw for slice, Fade for hook)
                </div>
              </div>
            </div>
          </div>
      </div>
    </Card>
  );
};

/**
 * MetricRow - Shows current vs optimized metric with visual change indicator
 */
const MetricRow = ({ label, current, optimized, unit, change, highlight = false, higherIsBetter = false }) => {
  const changeDisplay = change > 0 ? `+${change.toFixed(1)}` : change.toFixed(1);
  const isImprovement = higherIsBetter ? change > 0 : change < 0;
  const changeColor = highlight
    ? (change > 0 ? 'text-success' : 'text-zinc-500')
    : (isImprovement ? 'text-success' : 'text-warning');

  return (
    <div className="flex items-center justify-between p-2 bg-subtle/50 rounded">
      <div className="text-xs text-zinc-400">{label}</div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-zinc-500">{current}{unit}</span>
        <span className="text-zinc-600 text-xs">→</span>
        <span className="text-xs text-white font-medium">{optimized}{unit}</span>
        <span className={`text-xs font-medium ${changeColor} min-w-[50px] text-right`}>
          ({changeDisplay})
          {isImprovement && change !== 0 && ' ✓'}
        </span>
      </div>
    </div>
  );
};

export default OptimizationSuggestionCard;
